<script setup lang="ts">
import { ref } from "vue";

import { useGetUserOrganizationPerimeterQuery } from "@/composables/kili/consumption/queries/use-get-user-organization-perimeter-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { TOrganizationPerimeter } from "@/data/api/kili/consumption/types/user-organization-perimeter.type";
import { useTypedIntl } from "@/locales/typed-intl";

import UserPerimeterTree from "@/components/data/dashboard/user-perimeter-tree/user-perimeter-tree.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IEmits {
  (e: "submit", selection: TOrganizationPerimeter[]): void;
}

const emits = defineEmits<IEmits>();
const visible = defineModel<boolean>("visible");

const { t, k } = useTypedIntl();

const { data: userOrganizationPerimeter, isLoading: isUserOrganizationPerimeterLoading } =
  useGetUserOrganizationPerimeterQuery();

const selectedDivisions = ref<TOrganizationPerimeter[]>([]);
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    :data-test-id="DATA_TEST_IDS.views.dashboard.selectPerimeter.dialog.component"
    :header="t(k.data.dashboard.selectPerimeter.title)"
    modal
    size="medium"
  >
    <UserPerimeterTree
      v-model:selected-divisions="selectedDivisions"
      :is-loading="isUserOrganizationPerimeterLoading"
      :user-organization-perimeter="userOrganizationPerimeter"
    />

    <template #footer>
      <div class="w-full flex gap-2">
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.views.dashboard.selectPerimeter.dialog.cancelButton"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          @click="visible = false"
        />
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.views.dashboard.selectPerimeter.dialog.submitButton"
          :disabled="isUserOrganizationPerimeterLoading"
          @click="emits('submit', selectedDivisions)"
        >
          <i
            class="not-italic bg-white w-fit rounded-full text-brand py-0 px-[6px] text-xs mr-2"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.moveToButton.selectCount"
          >
            {{ selectedDivisions.length }}
          </i>
          {{ t(k.data.dashboard.selectPerimeter.submit) }}
        </HospitalityButton>
      </div>
    </template>
  </BaseDialog>
</template>
